import { defineStore } from 'pinia';
import type {
  CurrencyFormat,
  RedeemResult,
  WalletAuditRecord,
  WalletLedgerEntry,
  WithdrawPrecheck,
} from '@/entities/wallet/types';
import { redeemCode } from '@/features/promo/promo-api';
import {
  fetchAuditRecords,
  fetchCurrencyFormat,
  fetchWalletBalance,
  fetchWalletLedger,
  fetchWithdrawPrecheck,
} from '@/features/wallet/wallet-api';
import { reportEvent } from '@/shared/monitoring/monitoring';
import { DEFAULT_CURRENCY_FORMAT, formatMinor } from '@/shared/utils/money';
import { useSessionStore } from '@/stores/session-store';

interface WalletState {
  auditRecords: WalletAuditRecord[];
  balanceMinor: number;
  currency: string;
  entries: WalletLedgerEntry[];
  format: CurrencyFormat;
  frozenMinor: number;
  lastError: string;
  loaded: boolean;
  loading: boolean;
  pointsMinor: number;
  withdrawPrecheck: WithdrawPrecheck | null;
}

export const useWalletStore = defineStore('wallet', {
  actions: {
    reset() {
      this.balanceMinor = 0;
      this.pointsMinor = 0;
      this.frozenMinor = 0;
      this.entries = [];
      this.auditRecords = [];
      this.withdrawPrecheck = null;
      this.loaded = false;
      this.lastError = '';
    },
    async loadAll(force = false) {
      const sessionStore = useSessionStore();
      if (!sessionStore.token) return;
      if (this.loading) return;
      if (this.loaded && !force) return;

      this.loading = true;
      this.lastError = '';
      const token = sessionStore.token;
      try {
        const [format, balance, ledger] = await Promise.all([
          fetchCurrencyFormat(token),
          fetchWalletBalance(token),
          fetchWalletLedger(token, 20),
        ]);
        this.format = format;
        this.currency = balance.currency;
        this.balanceMinor = balance.balanceMinor;
        this.frozenMinor = balance.frozenMinor;
        this.pointsMinor = balance.pointsMinor ?? 0;
        this.entries = ledger.items;
        this.loaded = true;
      } catch (error) {
        this.lastError = error instanceof Error ? error.message : String(error);
        reportEvent({
          context: { error: this.lastError },
          event: 'wallet.load.failed',
          level: 'warn',
        });
      } finally {
        this.loading = false;
      }
    },
    /** 仅刷新余额（手动刷新按钮用）：不重拉币种格式/流水/稽核，只打 balance 接口。 */
    async loadBalance() {
      const sessionStore = useSessionStore();
      if (!sessionStore.token) return;
      try {
        const balance = await fetchWalletBalance(sessionStore.token);
        this.currency = balance.currency;
        this.balanceMinor = balance.balanceMinor;
        this.frozenMinor = balance.frozenMinor;
        this.pointsMinor = balance.pointsMinor ?? 0;
        this.loaded = true;
      } catch (error) {
        this.lastError = error instanceof Error ? error.message : String(error);
        reportEvent({
          context: { error: this.lastError },
          event: 'wallet.balance.refresh.failed',
          level: 'warn',
        });
      }
    },
    async loadAuditRecords() {
      const sessionStore = useSessionStore();
      if (!sessionStore.token) return;
      try {
        const [records, precheck] = await Promise.all([
          fetchAuditRecords(sessionStore.token),
          fetchWithdrawPrecheck(sessionStore.token),
        ]);
        this.auditRecords = records;
        this.withdrawPrecheck = precheck;
      } catch (error) {
        this.lastError = error instanceof Error ? error.message : String(error);
      }
    },
    async redeem(code: string): Promise<RedeemResult> {
      const sessionStore = useSessionStore();
      if (!sessionStore.token) {
        throw new Error('Sessão expirada');
      }
      const result = await redeemCode(sessionStore.token, { code });
      this.balanceMinor = result.balanceMinor;
      await Promise.all([this.loadAll(true), this.loadAuditRecords()]);
      return result;
    },
  },
  getters: {
    availableMinor: (state) => Math.max(0, state.balanceMinor - state.frozenMinor),
    balanceText: (state) => formatMinor(state.balanceMinor, state.format),
  },
  state: (): WalletState => ({
    auditRecords: [],
    balanceMinor: 0,
    currency: DEFAULT_CURRENCY_FORMAT.currency,
    entries: [],
    format: DEFAULT_CURRENCY_FORMAT,
    frozenMinor: 0,
    lastError: '',
    loaded: false,
    loading: false,
    pointsMinor: 0,
    withdrawPrecheck: null,
  }),
});
